/**
 * CCP — Infrastruktura Panel
 *
 * Správa celého ACM fleet. Lokální uzel je vždy přítomný a vždy online —
 * při nedostupnosti vzdálených uzlů CCP plně nahrazuje lokální dashboard.
 */
import { TaskFailureDetails } from '../../components/TaskFailureDetails';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { DEFAULT_API_PORT, DEFAULT_API_TLS_PORT, DEFAULT_UI_PORT } from '../../lib/node-ports';
import {
  assignNodeToGroup,
  createNodeGroup,
  deleteNodeGroup,
  loadNodeGroups,
  renameNodeGroup,
  type NodeGroupsState,
} from '../../lib/node-groups';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatCard,
  StatsGrid,
} from '../../components/ui';
import {
  AlertCircle,
  Box,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Database,
  ExternalLink,
  Globe,
  Layers,
  Loader2,
  Network,
  Package,
  Plus,
  Radio,
  RefreshCw,
  Rocket,
  Search,
  Server,
  Shield,
  ShieldAlert,
  Terminal,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '../../lib/themed-dialogs';
import i18n from '../../lib/i18n';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AcmNode {
  id: string;
  /** Wire routing key = stable_id (UUID / acm-…). Nikdy hostname. */
  name: string;
  /** Human label (hostname) — jen prezentace. */
  label: string;
  host: string;
  api_port: number;
  dashboard_port: number;
  api_key: string | null;
  enabled: boolean;
  notes: string | null;
  created_at: string;
  last_seen: string | null;
  is_local?: boolean;
  /** Z peers_list — announce age (stejný zdroj jako Mesh nody widget). */
  online?: boolean;
  status?: 'online' | 'offline' | string;
  announce_age_s?: number | null;
  /** Doplněno z mesh.topology — klasifikace a seskupení. */
  kind?: 'cluster' | 'remote';
  cluster_uid?: string | null;
  public_ui?: string | null;
  public_ui_published?: boolean;
  /** Jen z topologie, není v peers_list (např. mc01). */
  topology_only?: boolean;
  /** Role uzlu vůči mně — 'hub' = můj domovský hub, 'spoke' / 'peer' / 'foreign'. */
  role?: string | null;
  /** Postura vztahu ('upstream' u spoke→hub, 'cluster' v clusteru). */
  trust_mode?: string | null;
  /** Immutable logical ID — kanonický klíč karty. */
  stable_id?: string;
}

/** Kanonický klíč karty = jen stable_id / UUID, nikdy hostname. */
function nodeStableKey(n: {
  stable_id?: string | null;
  name?: string | null;
  id?: string | null;
}): string {
  return String(n.stable_id || n.name || n.id || '').trim();
}

function sortFleetNodes(a: AcmNode, b: AcmNode): number {
  // 1) lokál vždy první
  if (!!a.is_local !== !!b.is_local) return a.is_local ? -1 : 1;
  // 2) cluster před remote
  const ka = a.kind === 'remote' ? 1 : 0;
  const kb = b.kind === 'remote' ? 1 : 0;
  if (ka !== kb) return ka - kb;
  // 3) online first
  if (!!a.online !== !!b.online) return a.online ? -1 : 1;
  // 4) label (hostname), ne UUID
  return (a.label || a.name).localeCompare(b.label || b.name, 'cs');
}

interface TopoNodeLite {
  id?: string;
  route_name?: string;
  label?: string;
  host?: string | null;
  api_port?: number | null;
  dashboard_port?: number | null;
  health?: string;
  cluster_uid?: string | null;
  trust_mode?: string;
  public_ui?: string | null;
  public_ui_published?: boolean;
  announce_age_s?: number | null;
}

type GroupMode = 'cluster' | 'custom';

const NODE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Uzel zaregistrovaný jen routing identitou (capability routing přes hub relay)
 * nemá lidské jméno — label je pak rovný jeho UUID. Přes celou kartu vypadá
 * jako chyba, tak se zkrátí; plná hodnota zůstává v title.
 */
function nodeDisplayLabel(label?: string | null, fallback?: string | null): string {
  const value = (label ?? '').trim();
  if (value && !NODE_UUID_RE.test(value)) return value;
  const id = value || (fallback ?? '').trim();
  return id ? `${id.slice(0, 8)}…` : '—';
}

function clusterSectionLabel(
  uid: string | null | undefined,
  kind: 'cluster' | 'remote' | 'home_hub',
  t: (k: string, d: string, o?: Record<string, string>) => string,
): string {
  if (kind === 'cluster') {
    return t('mesh.sectionThisCluster', 'Tento cluster');
  }
  // Vlastní hub není anonymní „remote": uzel podle své role ví, že je to jeho
  // domovský hub (Filip 2026-08-25 — v panelu se kreslil jako
  // „Remote (bez cluster ID)").
  if (kind === 'home_hub') {
    return t('mesh.sectionHomeHub', 'Domovský hub');
  }
  if (!uid) {
    return t('mesh.sectionRemoteUngrouped', 'Remote (bez cluster ID)');
  }
  // kratší zobrazení UUID / cluster:name@domain
  const short = uid.startsWith('cluster:')
    ? uid.slice('cluster:'.length)
    : uid.length > 20
    ? `${uid.slice(0, 8)}…${uid.slice(-4)}`
    : uid;
  return t('mesh.sectionRemoteCluster', 'Remote · {{id}}', { id: short });
}

interface NodeModule {
  name: string;
  version: string;
  description: string;
  tools: string[];
}
interface NodeContainer {
  id: string;
  name: string;
  /** docker ps {{.State}} — running / exited / … */
  state: string;
  /** docker ps {{.Status}} — „Up 3 hours" */
  status: string;
  module: string;
  image: string;
}
interface BuildInfo {
  version: string;
  git_commit: string | null;
  git_date: string | null;
  git_branch: string | null;
  origin_repo: string | null;
  module_source_repo: string | null;
}
interface NodeStatus {
  ok: boolean;
  online: boolean;
  node: AcmNode;
  /** toolbox.health_status — souhrn self-testu, ne inventura uzlu. */
  health: {
    ok?: boolean;
    timestamp?: string;
    status?: string;
    tests?: Array<{ name: string; status: string; duration_ms?: number }>;
  } | null;
  modules: NodeModule[];
  containers: NodeContainer[];
  apphost?: unknown[];
  supabase?: unknown[];
  build?: BuildInfo | null;
  error?: string;
}

// ── AppHost tab ───────────────────────────────────────────────────────────────


// ── DBHost tab ────────────────────────────────────────────────────────────────


// ── Orchestrator tab ──────────────────────────────────────────────────────────


// ── Supabase tab ──────────────────────────────────────────────────────────────


// ── Node Card ─────────────────────────────────────────────────────────────────

function DiskUsageBar({
  free,
  total,
  freeHuman,
  totalHuman,
}: {
  free: number;
  total: number;
  freeHuman?: string;
  totalHuman?: string;
}) {
  const usedPct = total > 0 ? Math.round(((total - free) / total) * 100) : 0;
  const color = usedPct >= 90
    ? 'bg-red-500'
    : usedPct >= 75
    ? 'bg-amber-500'
    : usedPct >= 50
    ? 'bg-yellow-500'
    : 'bg-emerald-500';
  return (
    <div className='flex-1 flex items-center gap-2'>
      <div className='flex-1 h-1.5 rounded-full bg-gray-800 overflow-hidden'>
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${Math.min(100, usedPct)}%` }}
        />
      </div>
      <span className='text-[11px] font-mono text-gray-300 min-w-[80px] text-right'>
        {freeHuman ?? '?'}
        <span className='text-gray-600'>/ {totalHuman ?? '?'}</span>
      </span>
    </div>
  );
}

function NodePublicUrls({ node, availableTools }: { node: AcmNode; availableTools?: string[] }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['node-services', node.name],
    queryFn: async () => {
      type Svc = {
        ok: boolean;
        services: Array<
          { id: string; label: string; public_enabled: boolean; public_domain: string | null }
        >;
      };
      try {
        if (node.is_local) {
          return await api.mcp<Svc>('acm-self.services_state', {});
        }
        const r = await api.mcp<{ ok: boolean; result?: Svc; error?: string }>('mesh.rpc_call', {
          node: node.name,
          tool: 'acm-self.services_state',
          args: {},
        });
        if (!r.ok) return null;
        const res = r.result as unknown;
        const text = (res as { content?: Array<{ text?: string }> })?.content?.[0]?.text;
        if (typeof text === 'string') {
          try {
            return JSON.parse(text) as Svc;
          } catch {
            return null;
          }
        }
        return (res as Svc) ?? null;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
    retry: false,
    // Remote services_state is optional across mixed-version fleets. Calling
    // a tool the peer did not advertise creates a false audit error.
    enabled: node.is_local === true || availableTools?.includes('acm-self.services_state') === true,
  });
  const published = (data?.services ?? []).filter((s) => s.public_enabled && s.public_domain);
  if (published.length === 0) return null;
  return (
    <div className='space-y-1 mb-3'>
      <span className='text-[10px] text-gray-600'>{t('meshPeers.verejne')}</span>
      {published.map((s) => (
        <div key={s.id} className='flex items-center gap-2'>
          <Globe size={9} className='text-emerald-400' />
          <a
            href={`https://${s.public_domain}`}
            target='_blank'
            rel='noopener'
            onClick={(e) => e.stopPropagation()}
            className='text-[10px] font-mono text-emerald-400 hover:text-emerald-300 truncate'
          >
            {s.public_domain}
          </a>
          <span className='text-[10px] text-gray-600'>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function NodeCard({ node, onSelect }: { node: AcmNode; onSelect: () => void }) {
  const { t } = useTranslation();
  const wireId = nodeStableKey(node);
  const { data: st, isLoading } = useQuery({
    queryKey: ['ccp-node-status', wireId],
    queryFn: async () => {
      // Background poll — bez toastu (offline peer / timeout je stav, ne chyba uživatele).
      if (node.is_local) {
        const r = await api.mcp<Omit<NodeStatus, 'ok' | 'node'>>('mesh.local_status', {}, {
          silent: true,
        });
        return { ok: true, node, ...r } as NodeStatus;
      }
      // peers_status name = stable_id (UUID), ne hostname
      return api.mcp<NodeStatus>('mesh.peers_status', { name: wireId }, { silent: true });
    },
    refetchInterval: 30_000,
    retry: false,
  });
  // Cluster-wide retained MQTT registry — disk/RAM/CPU + version + IPv4/IPv6.
  // Sdílená query napříč všemi kartami (jednou per dashboard refetch).
  const { data: registry } = useQuery({
    queryKey: ['mesh-registry-nodes'],
    queryFn: async () => {
      try {
        return await api.mcp<{
          ok: boolean;
          nodes: Array<{
            node_id: string;
            state: {
              node?: {
                cpu_cores?: number;
                memory?: { free_human?: string; total_human?: string };
                disk?: {
                  free_human?: string;
                  total_human?: string;
                  free_bytes?: number;
                  total_bytes?: number;
                };
              };
            } | null;
            heartbeat: Record<string, unknown> | null;
            discovery: {
              server_version?: string;
              git_commit?: string;
              interfaces?: Array<
                { name: string; ipv4: Array<{ ip: string }>; ipv6: Array<{ ip: string }> }
              >;
            } | null;
            state_age_seconds: number | null;
          }>;
        }>('mesh.registry_nodes', {});
      } catch {
        return null;
      }
    },
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
  const reg = registry?.nodes?.find(
    (n) => n.node_id === wireId || n.node_id === node.name || n.node_id === node.label,
  );
  const sysNode = reg?.state?.node;
  const nodeModules = Array.isArray(st?.modules) ? st.modules : [];
  const nodeContainers = Array.isArray(st?.containers) ? st.containers : [];
  // Jedna pravda s Dashboard / peers_list: announce online je baseline.
  // Live mTLS peers_status jen potvrdí / upřesní (dřív selhání = falešný offline).
  const liveOk = st?.ok === true;
  const liveOnline = liveOk ? st?.online : undefined;
  const announceOnline = node.online === true || node.status === 'online'
    ? true
    : node.online === false || node.status === 'offline'
    ? false
    : undefined;
  const online = node.is_local
    ? true
    : liveOnline === true
    ? true
    : liveOnline === false && announceOnline === false
    ? false
    : liveOnline === false && announceOnline === true
    ? true // announce fresh, mTLS probe fail → stále „online“ (mesh viditelný)
    : announceOnline;
  const statusColor = online
    ? 'border-emerald-500/30'
    : online === false
    ? 'border-red-500/30'
    : 'border-gray-800';

  return (
    <div
      onClick={onSelect}
      className={`border ${statusColor} rounded-xl p-5 cursor-pointer transition-all hover:bg-gray-900/40 min-h-[140px]`}
    >
      {/* Header: status + name + badges */}
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2.5'>
          <div
            className={`w-2.5 h-2.5 rounded-full ${
              online ? 'bg-emerald-400' : online === false ? 'bg-red-400' : 'bg-gray-600'
            }`}
          />
          <span className='font-bold text-white text-sm' title={node.label || node.name}>
            {nodeDisplayLabel(node.label, node.name)}
          </span>
        </div>
        <div className='flex items-center gap-1.5 flex-wrap justify-end'>
          {node.is_local && <Badge variant='blue'>{t('meshPeers.tentoUzel')}</Badge>}
          {node.kind === 'remote' && (
            <Badge variant='yellow'>{t('mesh.remoteBadge', 'remote')}</Badge>
          )}
          {node.topology_only && (
            <Badge variant='gray'>{t('mesh.topologyOnly', 'topo')}</Badge>
          )}
          {!node.enabled && <Badge variant='yellow'>vypnuto</Badge>}
          {!node.is_local && online === false && (
            <Badge variant='yellow'>{t('meshPeers.meshUnreachable', 'mesh neodpovídá')}</Badge>
          )}
        </div>
      </div>

      {/* Adresy */}
      {(() => {
        const ip = node.is_local ? location.hostname : node.host;
        const apiPort = node.api_port || DEFAULT_API_TLS_PORT;
        const uiPort = node.dashboard_port || DEFAULT_UI_PORT;
        return (
          <div className='space-y-1 mb-3'>
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>API</span>
              <a
                href={`https://${ip}:${apiPort}`}
                target='_blank'
                rel='noopener'
                onClick={(e) => e.stopPropagation()}
                className='text-xs font-mono text-gray-400 hover:text-white'
              >
                {ip}:{apiPort}
              </a>
            </div>
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>UI</span>
              <a
                href={`https://${ip}:${uiPort}`}
                target='_blank'
                rel='noopener'
                onClick={(e) => e.stopPropagation()}
                className='text-xs font-mono text-blue-400 hover:text-blue-300'
              >
                {ip}:{uiPort}
              </a>
            </div>
          </div>
        );
      })()}

      {/* Veřejná UI z topology (catalog) — hned, bez čekání na peer tools */}
      {node.public_ui && (
        <div className='flex items-center gap-2 mb-2'>
          <span className='text-[10px] text-gray-600 w-10'>Pub</span>
          <a
            href={`https://${node.public_ui.replace(/^https?:\/\//i, '')}`}
            target='_blank'
            rel='noopener'
            onClick={(e) => e.stopPropagation()}
            className={`text-xs font-mono truncate ${
              node.public_ui_published
                ? 'text-blue-400 hover:text-blue-300'
                : 'text-amber-400/90 hover:text-amber-300'
            }`}
          >
            {node.public_ui}
            {!node.public_ui_published ? ' (pending)' : ''}
          </a>
        </div>
      )}

      {/* Veřejné adresy — z node services (doplnění) */}
      <NodePublicUrls
        node={node}
        availableTools={nodeModules.flatMap((module) => module.tools ?? [])}
      />

      {/* Resources — disk/RAM/CPU z retained MQTT registru */}
      {sysNode && (
        <div className='space-y-1 mb-3 mt-2 pt-2 border-t border-gray-800/60'>
          {sysNode.disk && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>Disk</span>
              <DiskUsageBar
                free={sysNode.disk.free_bytes ?? 0}
                total={sysNode.disk.total_bytes ?? 0}
                freeHuman={sysNode.disk.free_human}
                totalHuman={sysNode.disk.total_human}
              />
            </div>
          )}
          {sysNode.memory && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>RAM</span>
              <span className='text-[11px] font-mono text-gray-300'>
                {sysNode.memory.free_human ?? '?'}
                <span className='text-gray-600'>/ {sysNode.memory.total_human ?? '?'}</span>
              </span>
            </div>
          )}
          {sysNode.cpu_cores != null && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>CPU</span>
              <span className='text-[11px] font-mono text-gray-300'>
                {sysNode.cpu_cores} cores
              </span>
            </div>
          )}
          {reg?.state_age_seconds != null && reg.state_age_seconds > 180 && (
            <div
              className={`text-[10px] italic ${
                reg.state_age_seconds > 600 ? 'text-red-400' : 'text-amber-500'
              }`}
            >
              ⚠ data stará {reg.state_age_seconds}s
              {reg.state_age_seconds > 600
                ? t('meshPeers.nodeProbablyOffline', ' — node pravděpodobně offline')
                : t('meshPeers.dataDelayed', ' — data zpožděná')}
            </div>
          )}
        </div>
      )}

      {/* Verze/Build/Zdroj — vždy viditelné když st.build je dostupný (= živá data z mesh_local_status), nezávislé na MQTT retained sysNode */}
      {st?.build && (
        <div className='space-y-1 mb-3 mt-2 pt-2 border-t border-gray-800/60'>
          {(st.build.version || reg?.discovery?.server_version) && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>Verze</span>
              <span className='text-[11px] font-mono text-gray-500'>
                v{st.build.version ?? reg?.discovery?.server_version}
                {(st.build.git_commit || reg?.discovery?.git_commit) && (
                  <span className='text-gray-700 ml-1'>
                    · {(st.build.git_commit ?? reg?.discovery?.git_commit ?? '').slice(0, 8)}
                  </span>
                )}
                {st.build.git_branch && st.build.git_branch !== 'master' && (
                  <span className='text-amber-500 ml-1'>({st.build.git_branch})</span>
                )}
              </span>
            </div>
          )}
          {st.build.git_date && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>Build</span>
              <span className='text-[11px] font-mono text-gray-500'>
                {new Date(st.build.git_date).toLocaleString('cs', {
                  day: 'numeric',
                  month: 'short',
                  year: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          )}
          {(st.build.module_source_repo || st.build.origin_repo) && (
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-gray-600 w-10'>{t('meshpeers.zdroj', 'Zdroj')}</span>
              <span
                className='text-[11px] font-mono text-gray-500 truncate'
                title={st.build.module_source_repo ?? st.build.origin_repo ?? ''}
              >
                {(st.build.module_source_repo ?? st.build.origin_repo ?? '')
                  .replace(/^https?:\/\//, '')
                  .replace(/\.git$/, '')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Status info */}
      <div className='space-y-2'>
        {isLoading && <div className='text-[10px] text-gray-600'>{t('meshPeers.nacitamStav')}</div>}
        {st?.online && (
          <div className='flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-400'>
            <span className='flex items-center gap-1'>
              <Layers size={10} />
              <span className='text-white font-medium'>{nodeModules.length}</span>{' '}
              {t('meshPeers.modulu')}
            </span>
            <span className='flex items-center gap-1'>
              <Box size={10} />
              <span className='text-white font-medium'>
                {nodeContainers.filter((c) => c.state === 'running').length}
              </span>
              /{nodeContainers.length} {t('meshpeers.kontejneru', 'kontejnerů')}
            </span>
            {nodeModules.length > 0 && (
              <span className='flex items-center gap-1'>
                <Terminal size={10} />
                <span className='text-white font-medium'>
                  {nodeModules.reduce((sum, m) => sum + m.tools.length, 0)}
                </span>{' '}
                {t('meshPeers.toolu')}
              </span>
            )}
            {(st.apphost?.length ?? 0) > 0 && (
              <span className='flex items-center gap-1'>
                <Package size={10} />
                <span className='text-white font-medium'>{st.apphost?.length}</span>{' '}
                apphost
              </span>
            )}
            {(st.supabase?.length ?? 0) > 0 && (
              <span className='flex items-center gap-1'>
                <Database size={10} />
                <span className='text-white font-medium'>{st.supabase?.length}</span>{' '}
                supabase
              </span>
            )}
          </div>
        )}
        {st?.online && (
          <div className='flex gap-3 text-[10px] text-gray-500 mt-1'>
            {st.node?.created_at && (
              <span>
                up od {new Date(st.node.created_at).toLocaleString('cs', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {st.build?.git_commit && (
              <span className='font-mono'>{st.build.git_commit.slice(0, 8)}</span>
            )}
            {st.build?.git_date && (
              <span>
                build {new Date(st.build.git_date).toLocaleString('cs', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            )}
            {!st.build?.git_commit && <span>{t('meshPeers.acm20')}</span>}
          </div>
        )}
        {!isLoading && online === false && (
          <div className='flex items-center gap-1.5 text-[10px] text-red-400/80'>
            <WifiOff size={10} />
            <span>{t('meshPeers.nedostupny')}</span>
          </div>
        )}
        {!isLoading && online === undefined && !node.is_local && (
          <div className='flex items-center gap-1.5 text-[10px] text-gray-500'>
            <span>{t('meshPeers.stavNeznamy')}</span>
          </div>
        )}
        {node.last_seen && (
          <div className='text-[10px] text-gray-600'>
            naposledy {new Date(node.last_seen).toLocaleString('cs', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
      </div>

      {/* Click hint */}
      <div className='mt-3 pt-2 border-t border-gray-800/50 text-[10px] text-gray-600 flex items-center gap-1'>
        <ExternalLink size={9} /> {t('meshPeers.detailNastaveni')}
      </div>
    </div>
  );
}

// ── Add Node Modal ────────────────────────────────────────────────────────────

interface DiscoverResult {
  ok: boolean;
  reachable?: boolean;
  acm?: boolean;
  error?: string;
  node?: { id?: string; host?: string } | null;
  modules?: { name: string; tools: string[] }[];
  has_supabase?: boolean;
}

function AddNodeModal({ onClose, onAdded, initialHost = '', initialPort = String(DEFAULT_API_PORT) }: {
  onClose: () => void;
  onAdded: () => void;
  initialHost?: string;
  initialPort?: string;
}) {
  const { t } = useTranslation();
  const [ipv6, setIpv6] = useState(initialHost);
  const [port, setPort] = useState(initialPort);
  const [discovered, setDiscovered] = useState<DiscoverResult | null>(null);
  const [form, setForm] = useState({
    name: '',
    label: '',
    host: '',
    api_port: String(DEFAULT_API_PORT),
    dashboard_port: String(DEFAULT_UI_PORT),
    api_key: '',
    notes: '',
  });
  const inp =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const discoverMut = useMutation({
    mutationFn: () =>
      api.mcp<
        DiscoverResult & {
          discovered?: Array<{ node_id?: string; address?: string }>;
          rejected?: Array<{ address?: string; error?: string }>;
        }
      >('mesh.peers_discover', {
        host: ipv6.trim(),
        candidates: [ipv6.trim()],
      }),
    onSuccess: (r) => {
      const first = r.discovered?.[0];
      const mapped: DiscoverResult = {
        ok: r.ok,
        reachable: (r.discovered?.length ?? 0) > 0,
        acm: (r.discovered?.length ?? 0) > 0,
        error: r.error ?? r.rejected?.[0]?.error,
        node: first ? { id: first.node_id, host: first.address ?? ipv6.trim() } : null,
      };
      setDiscovered(mapped);
      if (mapped.ok && mapped.acm) {
        const suggested = (mapped.node?.id ?? ipv6.trim()).replace(/[^a-z0-9-]/g, '-').replace(
          /-+/g,
          '-',
        ).replace(/^-|-$/g, '');
        setForm((f) => ({
          ...f,
          host: ipv6.trim(),
          api_port: port,
          dashboard_port: String(Number(port) - 1),
          name: f.name || suggested,
          label: f.label || (mapped.node?.host ?? ipv6.trim()),
        }));
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const saveMut = useMutation({
    mutationFn: () =>
      api.mcp('mesh.peers_add', {
        ...form,
        api_port: Number(form.api_port),
        dashboard_port: Number(form.dashboard_port),
        api_key: form.api_key || undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: () => {
      toast.success(t('meshPeers.uzelPridan'));
      onAdded();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div
      className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl space-y-5'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between'>
          <h2 className='font-bold text-white flex items-center gap-2'>
            <Server size={15} /> {t('meshPeers.pridatVzdalenyAcmUzel')}
          </h2>
          <button onClick={onClose} className='text-gray-600 hover:text-white'>
            <X size={16} />
          </button>
        </div>
        <div className='rounded-xl border border-gray-700 bg-gray-800/40 p-4 space-y-3'>
          <div className='text-xs font-semibold text-gray-400 uppercase tracking-widest'>
            Discovery
          </div>
          <div className='flex gap-2'>
            <input
              value={ipv6}
              onChange={(e) => setIpv6(e.target.value)}
              placeholder={t('meshPeers.ipv6NeboIpv4Adresa')}
              className='flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono'
            />
            <input
              value={port}
              onChange={(e) => setPort(e.target.value)}
              type='number'
              placeholder={String(DEFAULT_API_PORT)}
              className='w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-center'
            />
            <button
              onClick={() => discoverMut.mutate()}
              disabled={!ipv6.trim() || discoverMut.isPending}
              className='flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 disabled:opacity-40 text-sm font-medium'
            >
              {discoverMut.isPending
                ? <Loader2 size={13} className='animate-spin' />
                : <Search size={13} />} Zjistit
            </button>
          </div>
          {discovered && (
            <div
              className={`rounded-lg p-3 text-xs ${
                discovered.ok && discovered.acm
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300'
                  : 'bg-red-500/10 border border-red-500/20 text-red-300'
              }`}
            >
              {discovered.ok && discovered.acm
                ? (
                  <div className='flex items-center gap-1.5 font-semibold'>
                    <CheckCircle2 size={12} /> {t('meshpeers.acmUzel', 'ACM uzel —')} {discovered.modules?.length ?? 0}{' '}
                    {t('meshpeers.modulu', 'modulů')}{discovered.has_supabase && ' · Supabase ✓'}
                  </div>
                )
                : (
                  <div className='flex items-center gap-1.5'>
                    <AlertCircle size={12} />
                    {discovered.error ?? (discovered.reachable
                      ? t('meshPeers.notAcmNode', 'Není ACM node')
                      : t('meshPeers.nedostupny', 'Nedostupný'))}
                  </div>
                )}
            </div>
          )}
        </div>
        <div className='space-y-3'>
          <div className='grid grid-cols-2 gap-3'>
            <label className='block'>
              <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.nazevSlug')}</div>
              <input
                value={form.name}
                onChange={set('name')}
                placeholder='acm-node1'
                className={inp}
              />
            </label>
            <label className='block'>
              <div className='text-xs text-gray-500 mb-1'>Label</div>
              <input
                value={form.label}
                onChange={set('label')}
                placeholder={t('meshPeers.node1')}
                className={inp}
              />
            </label>
          </div>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.host')}</div>
            <input
              value={form.host}
              onChange={set('host')}
              placeholder='10.119.251.116'
              className={`${inp} font-mono`}
            />
          </label>
          <div className='grid grid-cols-2 gap-3'>
            <label className='block'>
              <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.apiPort')}</div>
              <input
                value={form.api_port}
                onChange={set('api_port')}
                type='number'
                className={inp}
              />
            </label>
            <label className='block'>
              <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.dashboardPort')}</div>
              <input
                value={form.dashboard_port}
                onChange={set('dashboard_port')}
                type='number'
                className={inp}
              />
            </label>
          </div>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.apiKlic')}</div>
            <input
              value={form.api_key}
              onChange={set('api_key')}
              type='password'
              placeholder='adx_...'
              className={inp}
            />
          </label>
          <label className='block'>
            <div className='text-xs text-gray-500 mb-1'>{t('meshPeers.poznamky')}</div>
            <input value={form.notes} onChange={set('notes')} className={inp} />
          </label>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 rounded-lg border border-gray-700 text-sm text-gray-400 hover:text-white'
          >
            {t('meshPeers.zrusit')}
          </button>
          <div className='flex-1' />
          <button
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || !form.name || !form.host}
            className='px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-sm font-semibold text-white'
          >
            {saveMut.isPending && <Loader2 size={14} className='inline animate-spin mr-1' />}{' '}
            {t('meshpeers.pridatUzel', 'Přidat uzel')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Network Scan Panel ────────────────────────────────────────────────────────

interface ScanResult {
  ok: boolean;
  found: number;
  timed_out: boolean;
  log: string[];
  nodes: {
    host: string;
    acm: boolean;
    node?: { id?: string; host?: string } | null;
    modules?: string[];
    has_supabase?: boolean;
  }[];
}

function NetworkScanPanel({ onRegister }: { onRegister: (host: string, port?: number) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [cidr, setCidr] = useState('');
  const [port, setPort] = useState(String(DEFAULT_API_PORT));
  const [result, setResult] = useState<ScanResult | null>(null);

  const scanMut = useMutation({
    mutationFn: async () => {
      // mesh.scan_network accepts concrete IP candidates only (no CIDR ranges)
      const host = cidr.trim();
      if (!host || host.includes('/')) {
        throw new Error(t('meshPeers.scanNeedConcreteIp', 'Zadej konkrétní IP (CIDR rozsahy mesh.scan_network nepřijímá)'));
      }
      const r = await api.mcp<{
        ok: boolean;
        discovered?: Array<{ node_id?: string; address?: string }>;
        rejected?: Array<{ address?: string; error?: string }>;
        error?: string;
      }>('mesh.scan_network', { candidates: [host] });
      const found = r.discovered ?? [];
      return {
        ok: r.ok,
        found: found.length,
        timed_out: false,
        log: [
          `mesh.scan_network candidates=[${host}]`,
          ...(r.rejected ?? []).map((x) => `reject ${x.address}: ${x.error}`),
          ...found.map((x) => `found ${x.node_id ?? '?'} @ ${x.address}`),
        ],
        nodes: found.map((n) => ({
          host: n.address ?? host,
          // `discovered` = odpovědělo na ACM discovery, tedy ACM uzel
          acm: true,
          node: { id: n.node_id, host: n.address },
          modules: [] as string[],
          has_supabase: false,
        })),
      } satisfies ScanResult;
    },
    onSuccess: (r) => setResult(r),
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Card>
      <CardContent className='p-4'>
        <button
          onClick={() => setOpen((o) => !o)}
          className='flex items-center justify-between w-full text-xs font-semibold text-gray-400 uppercase tracking-wide hover:text-white transition-colors'
        >
          <span className='flex items-center gap-2'>
            <Radio size={13} /> {t('meshPeers.skenovatSit')}
          </span>
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {open && (
          <div className='mt-4 space-y-3'>
            <div className='flex gap-2'>
              <input
                value={cidr}
                onChange={(e) => setCidr(e.target.value)}
                placeholder={t('meshPeers.cidrAutoDetect')}
                className='flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 font-mono'
              />
              <input
                value={port}
                onChange={(e) => setPort(e.target.value)}
                type='number'
                placeholder={String(DEFAULT_API_PORT)}
                className='w-20 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 text-center'
              />
              <button
                onClick={() => scanMut.mutate()}
                disabled={scanMut.isPending}
                className='flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 disabled:opacity-40 text-sm font-medium whitespace-nowrap'
              >
                {scanMut.isPending
                  ? <Loader2 size={13} className='animate-spin' />
                  : <Radio size={13} />}
                {scanMut.isPending ? 'Skenuji…' : 'Skenovat'}
              </button>
            </div>
            <div className='text-[11px] text-gray-600'>
              {t('meshPeers.ipv4Prohleda24Ipv6')}
            </div>
            {result && (
              <div className='space-y-3 mt-2'>
                <div className='bg-gray-950 border border-gray-800 rounded-xl p-3 max-h-28 overflow-y-auto'>
                  {result.log.map((l, i) => (
                    <div key={i} className='text-[10px] font-mono text-gray-600'>{l}</div>
                  ))}
                  {result.timed_out && (
                    <div className='text-[10px] font-mono text-yellow-600'>
                      {t('meshPeers.prerusenoTimeout60S')}
                    </div>
                  )}
                </div>
                {result.found === 0
                  ? (
                    <div className='text-xs text-gray-500 text-center py-2'>
                      {t('meshPeers.zadneAcmUzlyNenalezeny')}
                    </div>
                  )
                  : (
                    <div className='space-y-2'>
                      <div className='text-xs text-gray-500'>{result.found} {t('meshpeers.acmUzlu', 'ACM uzlů:')}</div>
                      {result.nodes.map((n, i) => (
                        <div
                          key={i}
                          className='flex items-center justify-between border border-gray-800 rounded-lg p-3 bg-gray-900/20'
                        >
                          <div>
                            <code className='text-xs font-mono text-gray-300'>{n.host}</code>
                            <div className='text-[10px] text-gray-600 mt-0.5'>
                              {n.modules?.length ?? 0} {t('meshpeers.modulu', 'modulů')}
                              {n.has_supabase && (
                                <span className='ml-2 text-blue-400'>
                                  {t('meshPeers.supabase')}
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => onRegister(n.host, Number(port))}
                            className='flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-600/30 text-xs font-medium'
                          >
                            <Plus size={11} /> Registrovat
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Fleet Overview (nodes list) ───────────────────────────────────────────────

// ── Spawn wizard ──────────────────────────────────────────────────────────────

type SpawnStep = 'form' | 'checking' | 'check_result' | 'spawning' | 'progress';

interface CheckResult {
  ok: boolean;
  error?: string;
  os?: string;
  hostname?: string;
  disk_free_gb?: number;
  acm_installed?: boolean;
  warnings?: string[];
  ready_to_spawn?: boolean;
}
export function SpawnWizard(
  { onClose, onSpawned }: { onClose: () => void; onSpawned: () => void },
) {
  const { t } = useTranslation();
  const [step, setStep] = useState<SpawnStep>('form');
  const [form, setForm] = useState({
    host: '',
    ssh_user: 'root',
    ssh_password: '',
    node_name: '',
    node_label: '',
  });
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [spawnKey, setSpawnKey] = useState('');
  const [log, setLog] = useState('');
  const [done, setDone] = useState(false);

  // Poll spawn progress via mesh.spawn_node action=status
  const { data: statusData } = useQuery({
    queryKey: ['spawn-status', spawnKey],
    queryFn: () =>
      api.mcp<{ ok: boolean; running: boolean; done: boolean; log: string }>(
        'mesh.spawn_node',
        { action: 'status', spawn_key: spawnKey },
      ),
    enabled: step === 'progress' && !!spawnKey,
    refetchInterval: step === 'progress' && !done ? 3000 : false,
  });
  if (statusData && step === 'progress') {
    if (statusData.log !== log) setLog(statusData.log);
    if (statusData.done && !done) {
      setDone(true);
    }
  }

  const checkMut = useMutation({
    mutationFn: () =>
      api.mcp<CheckResult & { ok: boolean }>('mesh.spawn_node', {
        action: 'check',
        host: form.host,
        ssh_user: form.ssh_user,
        ssh_password: form.ssh_password,
      }),
    onSuccess: (r) => {
      setCheckResult(r);
      setStep('check_result');
    },
    onError: (e: Error) => {
      setCheckResult({ ok: false, error: e.message });
      setStep('check_result');
    },
  });

  const spawnMut = useMutation({
    mutationFn: () =>
      api.mcp<{ ok: boolean; spawn_key?: string; error?: string; warning?: string }>(
        'mesh.spawn_node',
        { ...form, action: 'spawn', confirmed: true },
      ),
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? r.warning ?? i18n.t('meshpeers.spawnSelhal', 'Spawn selhal'));
        return;
      }
      setSpawnKey(r.spawn_key ?? '');
      setStep('progress');
      toast.info(t('meshPeers.spawnSpustenSledujtePrubeh'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const inp =
    'w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500';

  return (
    <div className='fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto'>
        <div className='flex items-center justify-between mb-5'>
          <div className='flex items-center gap-2'>
            <Rocket size={18} className='text-blue-400' />
            <h3 className='text-lg font-semibold text-white'>
              {t('meshPeers.spawnNovehoAcmServeru')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className='p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800'
          >
            <X size={16} />
          </button>
        </div>

        {/* Step: form */}
        {step === 'form' && (
          <div className='space-y-4'>
            <div className='text-xs text-gray-500 bg-gray-800/60 rounded-xl px-4 py-3'>
              {t(
                'meshPeers.spawnCredentialsHint',
                'Zadejte přístupové údaje k cílovému stroji. Systém ověří OS (Debian 12+) a nainstaluje ACM.',
              )}
            </div>
            <div>
              <label className='text-xs text-gray-500 mb-1 block'>
                {t('meshPeers.ipAdresaCilovehoStroje')}
              </label>
              <input
                value={form.host}
                onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                className={inp}
                placeholder='10.0.0.100'
              />
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>
                  {t('meshPeers.sshUzivatel')}
                </label>
                <input
                  value={form.ssh_user}
                  onChange={(e) => setForm((f) => ({ ...f, ssh_user: e.target.value }))}
                  className={inp}
                  placeholder='root'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>
                  {t('meshPeers.sshHeslo')}
                </label>
                <input
                  type='password'
                  value={form.ssh_password}
                  onChange={(e) => setForm((f) => ({ ...f, ssh_password: e.target.value }))}
                  className={inp}
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>
                  {t('meshPeers.nazevUzlu')}{' '}
                  <span className='text-gray-600'>{t('meshPeers.slug')}</span>
                </label>
                <input
                  value={form.node_name}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      node_name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                    }))}
                  className={inp}
                  placeholder='acm-node2'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500 mb-1 block'>Popisek</label>
                <input
                  value={form.node_label}
                  onChange={(e) => setForm((f) => ({ ...f, node_label: e.target.value }))}
                  className={inp}
                  placeholder={t('meshPeers.acmNode2')}
                />
              </div>
            </div>
            <div className='flex gap-3 pt-2'>
              <button
                onClick={onClose}
                className='flex-1 py-2 rounded-xl bg-gray-800 text-gray-300 text-sm hover:bg-gray-700'
              >
                {t('meshPeers.zrusit')}
              </button>
              <button
                onClick={() => {
                  setStep('checking');
                  checkMut.mutate();
                }}
                disabled={!form.host || !form.ssh_user || !form.ssh_password || !form.node_name ||
                  checkMut.isPending}
                className='flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-50'
              >
                {t('meshPeers.overitCilovyStroj')}
              </button>
            </div>
          </div>
        )}

        {/* Step: checking */}
        {step === 'checking' && (
          <div className='flex flex-col items-center py-10 gap-4'>
            <Loader2 size={32} className='animate-spin text-blue-400' />
            <div className='text-white font-medium'>{t('meshpeers.overuji', 'Ověřuji')} {form.host}...</div>
            <div className='text-xs text-gray-500'>{t('meshPeers.kontrolujiSshPripojeniA')}</div>
          </div>
        )}

        {/* Step: check result */}
        {step === 'check_result' && checkResult && (
          <div className='space-y-4'>
            {checkResult.ok
              ? (
                <>
                  <div className='flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3'>
                    <CheckCircle2 size={16} className='text-green-400 shrink-0' />
                    <div>
                      <div className='text-sm font-medium text-green-400'>
                        {t('meshPeers.cilovyStrojJePripraven')}
                      </div>
                      <div className='text-xs text-gray-400 mt-0.5'>
                        {checkResult.os} · {checkResult.hostname} · {checkResult.disk_free_gb}{' '}
                        {t('meshpeers.gbVolnehoMista', 'GB volného místa')}
                      </div>
                    </div>
                  </div>
                  {(checkResult.warnings?.length ?? 0) > 0 && (
                    <div className='space-y-2'>
                      {checkResult.warnings!.map((w, i) => (
                        <div
                          key={i}
                          className='flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl px-4 py-2.5'
                        >
                          <ShieldAlert size={14} className='text-yellow-400 shrink-0 mt-0.5' />
                          <span className='text-sm text-yellow-300'>{w}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className='bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-300'>
                    <strong>{t('meshPeers.upozorneni')}</strong> {t('meshPeers.spawnPrepiseObsah')}
                    {' '}
                    <code>/opt/acm</code>{' '}
                    {t('meshpeers.nainstalujeNoveSystemoveSluzby', 'a nainstaluje nové systémové služby. Existující data mohou být ztracena.\n                    Pokračujte pouze pokud s tím souhlasíte.')}
                  </div>
                </>
              )
              : (
                <div className='flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3'>
                  <AlertCircle size={16} className='text-red-400 shrink-0 mt-0.5' />
                  <div>
                    <div className='text-sm font-medium text-red-400'>
                      {t('meshPeers.overeniSelhalo')}
                    </div>
                    <div className='text-xs text-gray-400 mt-0.5'>{checkResult.error}</div>
                  </div>
                </div>
              )}
            <div className='flex gap-3'>
              <button
                onClick={() => setStep('form')}
                className='flex-1 py-2 rounded-xl bg-gray-800 text-gray-300 text-sm hover:bg-gray-700'
              >
                {t('meshPeers.zpet')}
              </button>
              {checkResult.ok && checkResult.ready_to_spawn && (
                <button
                  onClick={() => {
                    spawnMut.mutate();
                  }}
                  disabled={spawnMut.isPending}
                  className='flex-1 py-2 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-medium disabled:opacity-50'
                >
                  {spawnMut.isPending
                    ? (
                      <>
                        <Loader2 size={13} className='inline animate-spin mr-1' />
                        {t('meshPeers.spoustim')}
                      </>
                    )
                    : '🚀 Spustit Spawn'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step: progress */}
        {step === 'progress' && (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              {!done
                ? <Loader2 size={14} className='animate-spin text-blue-400' />
                : <CheckCircle2 size={14} className='text-green-400' />}
              <span className='text-sm text-white'>
                {done
                  ? t('meshPeers.spawnDone', 'Spawn dokončen')
                  : t('meshPeers.spawnInProgress', 'Probíhá: {{name}} @ {{host}}', {
                    name: form.node_name,
                    host: form.host,
                  })}
              </span>
            </div>
            <pre className='bg-gray-950 border border-gray-800 rounded-lg p-3 text-xs font-mono text-gray-300 whitespace-pre-wrap overflow-y-auto max-h-64'>
              {log || t('meshPeers.waitingOutput', '— čekám na výstup —')}
            </pre>
            {done && (
              <button
                onClick={() => {
                  onSpawned();
                  onClose();
                }}
                className='w-full py-2 rounded-xl bg-green-700 hover:bg-green-600 text-white text-sm font-medium'
              >
                {t('meshPeers.doneClose', 'Hotovo, zavřít')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FleetPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [showSpawn, setShowSpawn] = useState(false);
  const [prefillHost, setPrefillHost] = useState('');
  const [prefillPort, setPrefillPort] = useState('');
  const [groupMode, setGroupMode] = useState<GroupMode>(() => {
    try {
      return localStorage.getItem('acm_node_group_mode') === 'custom' ? 'custom' : 'cluster';
    } catch {
      return 'cluster';
    }
  });
  const [groupState, setGroupState] = useState<NodeGroupsState>(() => loadNodeGroups());
  const [newGroupName, setNewGroupName] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['ccp-nodes'],
    queryFn: () =>
      api.mcp<{ ok: boolean; nodes: AcmNode[] }>('mesh.peers_list', { include_local: true }),
  });

  // Topologie = kanonický inventář (cluster + remote včetně nodů mimo peers_list, např. mc01)
  const topoQ = useQuery({
    queryKey: ['mesh-topology'],
    queryFn: () =>
      api.mcp<{
        ok?: boolean;
        cluster?: TopoNodeLite[];
        remote?: TopoNodeLite[];
        summary?: { remote_total?: number; cluster_total?: number };
      }>('mesh.topology', {}),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  /**
   * peers_list ⊕ topology → 1 NodeCard na stable_id.
   * Klíč karty = jen UUID / stable_id. Hostname (route_name, label) je jen text.
   * Dřív byName.get(route_name) při peer.name=UUID → 2× každá karta.
   */
  const fleetNodes: AcmNode[] = useMemo(() => {
    const peers = data?.nodes ?? [];
    /** Map: stable_id → karta */
    const byId = new Map<string, AcmNode>();

    for (const p of peers) {
      const sid = nodeStableKey(p);
      if (!sid) continue;
      const human = (p.label && p.label !== sid ? p.label : null) || p.label || sid;
      byId.set(sid, {
        ...p,
        id: sid,
        name: sid,
        stable_id: sid,
        label: human,
        kind: 'cluster',
        cluster_uid: null,
      });
    }

    const applyTopo = (rows: TopoNodeLite[] | undefined, kind: 'cluster' | 'remote') => {
      for (const r of rows ?? []) {
        // topology.id = stable_id; route_name/label = hostname (NE klíč)
        const sid = String(r.id || '').trim();
        if (!sid) continue; // bez UUID nepřidávat kartu (žádné hostname-only duplicity)

        const routeName = String(r.route_name || '').trim();
        const human = String(r.label || routeName || '').trim();
        const online = r.health === 'online' || r.health === 'degraded'
          ? true
          : r.health === 'offline'
          ? false
          : undefined;

        const existing = byId.get(sid);
        if (existing) {
          existing.kind = kind;
          existing.cluster_uid = r.cluster_uid ?? existing.cluster_uid ?? null;
          existing.host = existing.host || r.host || existing.host;
          existing.api_port = existing.api_port || r.api_port || existing.api_port;
          existing.dashboard_port = existing.dashboard_port || r.dashboard_port ||
            existing.dashboard_port;
          existing.public_ui = r.public_ui ?? existing.public_ui;
          existing.public_ui_published = r.public_ui_published ??
            existing.public_ui_published;
          if (online !== undefined) {
            existing.online = online;
            existing.status = online ? 'online' : 'offline';
          }
          existing.announce_age_s = r.announce_age_s ?? existing.announce_age_s;
          // label = hostname; lokál si nechá „(lokální)“ z peers_list
          if (human && human !== sid && !existing.is_local) existing.label = human;
          existing.name = sid;
          existing.stable_id = sid;
          existing.id = sid;
        } else {
          byId.set(sid, {
            id: sid,
            name: sid,
            stable_id: sid,
            label: human && human !== sid ? human : sid,
            host: r.host || '',
            api_port: r.api_port || DEFAULT_API_TLS_PORT,
            dashboard_port: r.dashboard_port || DEFAULT_UI_PORT,
            api_key: null,
            enabled: true,
            notes: null,
            created_at: '',
            last_seen: null,
            is_local: false,
            online,
            status: online === true ? 'online' : online === false ? 'offline' : undefined,
            announce_age_s: r.announce_age_s ?? null,
            kind,
            cluster_uid: r.cluster_uid ?? null,
            public_ui: r.public_ui ?? null,
            public_ui_published: r.public_ui_published ?? false,
            topology_only: true,
          });
        }
      }
    };

    applyTopo(topoQ.data?.cluster, 'cluster');
    applyTopo(topoQ.data?.remote, 'remote');

    return [...byId.values()].sort(sortFleetNodes);
  }, [data?.nodes, topoQ.data]);

  const remoteCount = fleetNodes.filter((n) => n.kind === 'remote').length;
  const clusterCount = fleetNodes.filter((n) => n.kind !== 'remote').length;

  type Section = { key: string; title: string; accent: 'default' | 'amber' | 'violet'; nodes: AcmNode[] };

  const sections: Section[] = useMemo(() => {
    if (groupMode === 'custom' && groupState.groups.length > 0) {
      const used = new Set<string>();
      const out: Section[] = [];
      for (const g of groupState.groups) {
        const members = fleetNodes
          .filter((n) => {
            const sid = nodeStableKey(n);
            return groupState.membership[sid] === g.id ||
              groupState.membership[n.name] === g.id;
          })
          .sort(sortFleetNodes);
        members.forEach((n) => used.add(nodeStableKey(n)));
        out.push({
          key: `g-${g.id}`,
          title: g.name,
          accent: 'violet',
          nodes: members,
        });
      }
      const restCluster = fleetNodes
        .filter((n) => !used.has(nodeStableKey(n)) && n.kind !== 'remote')
        .sort(sortFleetNodes);
      const restRemote = fleetNodes
        .filter((n) => !used.has(nodeStableKey(n)) && n.kind === 'remote')
        .sort(sortFleetNodes);
      if (restCluster.length) {
        out.push({
          key: 'ungrouped-cluster',
          title: t('mesh.ungroupedCluster', 'Bez skupiny · cluster'),
          accent: 'default',
          nodes: restCluster,
        });
      }
      if (restRemote.length) {
        out.push({
          key: 'ungrouped-remote',
          title: t('mesh.ungroupedRemote', 'Bez skupiny · remote'),
          accent: 'amber',
          nodes: restRemote,
        });
      }
      return out;
    }

    // Podle cluster_uid: tento cluster pohromadě, každý remote cluster zvlášť.
    //
    // Členství se určuje z cluster_uid lokálního uzlu, ne z pořadí, v jakém
    // dorazila topologie. Peer, který v topologii vůbec není (typicky vlastní
    // hub přidaný přes capability routing), dostával výchozí kind 'cluster'
    // a tvářil se jako člen tohoto clusteru, i když o jeho clusteru nic nevíme.
    const localClusterUid = fleetNodes.find((n) => n.is_local)?.cluster_uid ?? null;
    const belongsToThisCluster = (n: AcmNode) =>
      !!n.is_local ||
      (!!localClusterUid && !!n.cluster_uid && n.cluster_uid === localClusterUid);

    const map = new Map<string, Section>();
    for (const n of fleetNodes) {
      // Domovský hub se pozná z ROLE, ne z cluster_uid — spoke o clusteru svého
      // hubu vědět nemusí, ale ví, že je to jeho hub.
      const isHomeHub = !n.is_local &&
        ((n.role ?? '') === 'hub' || (n.trust_mode ?? '') === 'hub');
      const kind: 'cluster' | 'remote' | 'home_hub' = isHomeHub
        ? 'home_hub'
        : belongsToThisCluster(n)
        ? 'cluster'
        : 'remote';
      const uidKey = kind === 'cluster'
        ? 'local-cluster'
        : kind === 'home_hub'
        ? 'home-hub'
        : `remote:${n.cluster_uid || nodeStableKey(n)}`;
      let sec = map.get(uidKey);
      if (!sec) {
        sec = {
          key: uidKey,
          title: clusterSectionLabel(n.cluster_uid, kind, t as never),
          accent: kind === 'remote' ? 'amber' : 'default',
          nodes: [],
        };
        map.set(uidKey, sec);
      }
      sec.nodes.push(n);
    }
    // pořadí: local cluster first, then remotes by title; uvnitř sekce sortFleetNodes
    const arr = [...map.values()];
    for (const sec of arr) sec.nodes.sort(sortFleetNodes);
    // „Tento cluster" má smysl jen když v něm někdo další je. Samostatný uzel
    // (typicky spoke) není cluster — Filip 2026-08-25: „jeden remote nod není
    // cluster, ne?"
    const localSec = map.get('local-cluster');
    if (localSec && localSec.nodes.length < 2) {
      localSec.title = t('mesh.sectionThisNode', 'Tento uzel') as string;
    }
    arr.sort((a, b) => {
      if (a.key === 'local-cluster') return -1;
      if (b.key === 'local-cluster') return 1;
      // Domovský hub hned za vlastním clusterem — je to nejbližší okolí uzlu.
      if (a.key === 'home-hub') return -1;
      if (b.key === 'home-hub') return 1;
      return a.title.localeCompare(b.title, 'cs');
    });
    return arr;
  }, [fleetNodes, groupMode, groupState, t]);

  const setMode = (m: GroupMode) => {
    setGroupMode(m);
    try {
      localStorage.setItem('acm_node_group_mode', m);
    } catch { /* */ }
  };

  const handleRegister = (host: string, port?: number) => {
    setPrefillHost(host);
    setPrefillPort(port ? String(port) : String(DEFAULT_API_PORT));
    setShowAdd(true);
  };

  const openNode = (n: AcmNode) => {
    // Workspace URL = stable_id (UUID), ne hostname
    const sid = nodeStableKey(n);
    const id = n.is_local ? 'local' : encodeURIComponent(sid);
    navigate(`/infra/nodes/${id}`);
  };

  const renderGrid = (list: AcmNode[]) => (
    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-2'>
      {list.map((n) => {
        const sid = nodeStableKey(n);
        return (
        <div key={sid} className='relative'>
          <NodeCard node={n} onSelect={() => openNode(n)} />
          {groupMode === 'custom' && (
            <div
              className='absolute top-2 right-2 z-10'
              onClick={(e) => e.stopPropagation()}
            >
              <select
                className='text-[10px] rounded-lg border border-gray-700 bg-gray-950/90 text-gray-300 px-1.5 py-1 max-w-[9rem]'
                value={groupState.membership[sid] ?? groupState.membership[n.name] ?? ''}
                title={t('mesh.assignGroup', 'Skupina')}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setGroupState(assignNodeToGroup(sid, v, groupState));
                }}
              >
                <option value=''>{t('mesh.noGroup', '— skupina —')}</option>
                {groupState.groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );

  const sectionChrome = (accent: Section['accent']) =>
    accent === 'amber'
      ? 'rounded-xl border border-amber-500/25 p-3 bg-amber-500/5'
      : accent === 'violet'
      ? 'rounded-xl border border-violet-500/25 p-3 bg-violet-500/5'
      : 'rounded-xl border border-gray-800/80 p-3 bg-gray-900/20';

  const sectionTitleColor = (accent: Section['accent']) =>
    accent === 'amber'
      ? 'text-amber-300'
      : accent === 'violet'
      ? 'text-violet-300'
      : 'text-gray-400';

  return (
    <div className='space-y-5'>
      <StatsGrid columns={3}>
        <StatCard label={t('meshPeers.uzlyCelkem')} value={fleetNodes.length} icon={Server} />
        <StatCard
          label={t('meshPeers.vzdalene')}
          value={remoteCount}
          icon={Network}
          variant='info'
        />
        <StatCard
          label={t('mesh.clusterNodes', 'Cluster')}
          value={clusterCount}
          icon={Wifi}
          variant='success'
        />
      </StatsGrid>

      <div>
        <div className='flex items-center justify-between mb-2 flex-wrap gap-2'>
          <div className='text-xs text-gray-600 uppercase tracking-widest font-semibold'>
            {t('meshPeers.uzlySection', 'Uzly')} ({fleetNodes.length})
          </div>
          <div className='flex flex-wrap gap-2 items-center'>
            <div className='flex rounded-lg border border-gray-800 overflow-hidden text-[11px]'>
              <button
                type='button'
                onClick={() => setMode('cluster')}
                className={`px-2.5 py-1.5 transition-colors ${
                  groupMode === 'cluster'
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {t('mesh.groupByCluster', 'Podle clusteru')}
              </button>
              <button
                type='button'
                onClick={() => setMode('custom')}
                className={`px-2.5 py-1.5 transition-colors border-l border-gray-800 ${
                  groupMode === 'custom'
                    ? 'bg-gray-800 text-white'
                    : 'text-gray-500 hover:text-gray-300'
                }`}
              >
                {t('mesh.groupByCustom', 'Moje skupiny')}
              </button>
            </div>
            <button
              onClick={() => setShowSpawn(true)}
              className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-600/20 border border-green-500/30 text-green-400 hover:bg-green-600/30 text-xs font-medium transition-colors'
            >
              <Rocket size={12} /> {t('meshPeers.spawnAcm')}
            </button>
            <button
              onClick={() => {
                setPrefillHost('');
                setPrefillPort('');
                setShowAdd(true);
              }}
              className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 text-xs font-medium transition-colors'
            >
              <Plus size={12} /> {t('meshPeers.pridatUzel')}
            </button>
          </div>
        </div>

        {groupMode === 'custom' && (
          <div className='flex flex-wrap items-center gap-2 mb-3 p-2 rounded-xl border border-gray-800 bg-gray-900/40'>
            <input
              className='bg-black/30 border border-gray-700 rounded-lg px-2 py-1 text-xs text-gray-200 min-w-[10rem]'
              placeholder={t('mesh.newGroupPlaceholder', 'Název nové skupiny…')}
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newGroupName.trim()) {
                  setGroupState(createNodeGroup(newGroupName, groupState));
                  setNewGroupName('');
                }
              }}
            />
            <button
              type='button'
              disabled={!newGroupName.trim()}
              onClick={async () => {
                setGroupState(createNodeGroup(newGroupName, groupState));
                setNewGroupName('');
              }}
              className='px-2.5 py-1 rounded-lg text-xs bg-violet-600/30 text-violet-200 border border-violet-500/30 disabled:opacity-40'
            >
              {t('mesh.createGroup', 'Vytvořit skupinu')}
            </button>
            {groupState.groups.map((g) => (
              <span
                key={g.id}
                className='inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-200'
              >
                <button
                  type='button'
                  className='hover:text-white'
                  title={t('mesh.renameGroup', 'Přejmenovat')}
                  onClick={async () => {
                    const name = await themedPrompt(
                      t('mesh.renameGroupPrompt', 'Nový název skupiny'),
                      g.name,
                    );
                    if (name) setGroupState(renameNodeGroup(g.id, name, groupState));
                  }}
                >
                  {g.name}
                </button>
                <button
                  type='button'
                  className='text-violet-400/70 hover:text-red-400 ml-0.5'
                  title={t('mesh.deleteGroup', 'Smazat skupinu')}
                  onClick={async () => {
                    if (
                      await themedConfirm(
                        t('mesh.deleteGroupConfirm', 'Smazat skupinu „{{name}}“?', {
                          name: g.name,
                        }),
                      )
                    ) {
                      setGroupState(deleteNodeGroup(g.id, groupState));
                    }
                  }}
                >
                  ×
                </button>
              </span>
            ))}
            {groupState.groups.length === 0 && (
              <span className='text-[11px] text-gray-500'>
                {t(
                  'mesh.customGroupsHint',
                  'Vytvoř skupinu a u karet vyber příslušnost.',
                )}
              </span>
            )}
          </div>
        )}

        <NetworkScanPanel onRegister={handleRegister} />

        {(isLoading || topoQ.isLoading) && fleetNodes.length === 0 && (
          <div className='py-8 text-center text-gray-500 text-sm'>
            <Loader2 size={14} className='inline animate-spin mr-2' />
            {t('meshPeers.nacitam')}
          </div>
        )}

        {fleetNodes.length === 0 && !isLoading && !topoQ.isLoading && (
          <div className='py-10 text-center border border-dashed border-gray-800 rounded-xl mt-3'>
            <Server size={28} className='text-gray-700 mx-auto mb-2' />
            <div className='text-sm text-gray-500'>{t('meshPeers.zadneVzdaleneUzly')}</div>
            <div className='text-xs text-gray-600 mt-1'>
              {t('meshPeers.pridejVzdalenyAcmUzel')}
            </div>
          </div>
        )}

        {fleetNodes.length > 0 && (
          <div className='space-y-4 mt-3'>
            {sections.map((sec) => (
              <div key={sec.key} className={sectionChrome(sec.accent)}>
                <div className='flex items-center gap-2 mb-1 flex-wrap'>
                  {sec.accent === 'amber'
                    ? <Network size={13} className='text-amber-400' />
                    : sec.accent === 'violet'
                    ? <Server size={13} className='text-violet-400' />
                    : <Server size={13} className='text-emerald-400' />}
                  <span
                    className={`text-[11px] font-semibold uppercase tracking-wider ${sectionTitleColor(sec.accent)}`}
                  >
                    {sec.title}
                  </span>
                  <span className='text-[10px] text-gray-600'>{sec.nodes.length}</span>
                </div>
                {sec.nodes.length > 0
                  ? renderGrid(sec.nodes)
                  : (
                    <div className='text-xs text-gray-600 py-3 text-center'>
                      {t('mesh.emptySection', 'Žádné nody v této skupině')}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddNodeModal
          initialHost={prefillHost}
          initialPort={prefillPort}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            qc.invalidateQueries({ queryKey: ['ccp-nodes'] });
            qc.invalidateQueries({ queryKey: ['mesh-topology'] });
          }}
        />
      )}
      {showSpawn && (
        <SpawnWizard
          onClose={() => setShowSpawn(false)}
          onSpawned={() => {
            setShowSpawn(false);
            qc.invalidateQueries({ queryKey: ['ccp-nodes'] });
            qc.invalidateQueries({ queryKey: ['mesh-topology'] });
          }}
        />
      )}
    </div>
  );
}

// ── ACM Services Panel — public flags ────────────────────────────────────────


// ── Security Panel — findings z certmgr scan ───────────────────────────────

interface SecurityFinding {
  id?: number;
  resource?: string;
  resource_id?: string;
  category?: string;
  source?: string;
  severity: 'critical' | 'high' | 'warning' | 'info' | 'med' | 'low';
  code: string;
  message: string;
  details?: Record<string, unknown>;
  resolved_at: string | null;
  first_seen?: string;
  last_seen?: string;
  action_url?: string | null;
}

export function SecurityPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: resp } = useQuery<{ ok: boolean; findings: SecurityFinding[] }>({
    queryKey: ['security-findings'],
    queryFn: () => api.mcp('toolbox.security_findings_all', {}),
    refetchInterval: 60_000,
  });
  const findings = resp?.findings ?? [];

  const runScan = useMutation({
    mutationFn: () => api.mcp('toolbox.security_scan', {}),
    onSuccess: () => {
      toast.success(t('meshPeers.scanDokoncen'));
      qc.invalidateQueries({ queryKey: ['security-findings'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (findings.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>{t('meshPeers.securityAudit')}</CardTitle>
          <button
            onClick={() => runScan.mutate()}
            disabled={runScan.isPending}
            className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white'
          >
            <RefreshCw size={12} /> {t('meshPeers.spustitScan')}
          </button>
        </CardHeader>
        <CardContent>
          <div className='flex items-center gap-2 py-2 text-sm text-emerald-400'>
            <Shield size={14} /> {t('meshPeers.zadneAktivniFindings')}
          </div>
        </CardContent>
      </Card>
    );
  }

  const severityMeta = (s: string) => ({
    critical: {
      label: 'CRITICAL',
      badge: 'bg-red-500/15 text-red-300 border-red-500/35',
      bar: 'bg-red-500',
    },
    high: {
      label: 'HIGH',
      badge: 'bg-orange-500/15 text-orange-300 border-orange-500/35',
      bar: 'bg-orange-500',
    },
    warning: {
      label: 'WARNING',
      badge: 'bg-amber-500/15 text-amber-300 border-amber-500/35',
      bar: 'bg-amber-500',
    },
    med: {
      label: 'MED',
      badge: 'bg-amber-500/15 text-amber-300 border-amber-500/35',
      bar: 'bg-amber-500',
    },
    low: {
      label: 'LOW',
      badge: 'bg-slate-500/15 text-slate-300 border-slate-500/35',
      bar: 'bg-slate-500',
    },
    info: {
      label: 'INFO',
      badge: 'bg-blue-500/15 text-blue-300 border-blue-500/35',
      bar: 'bg-blue-500',
    },
  }[s] || {
    label: s.toUpperCase(),
    badge: 'bg-gray-500/15 text-gray-300 border-gray-500/35',
    bar: 'bg-gray-500',
  });
  const targetLabel = (f: SecurityFinding) => {
    const file = typeof f.details?.file === 'string' ? f.details.file : null;
    const line = typeof f.details?.line === 'number' ? `:${f.details.line}` : '';
    if (file) return `${file}${line}`;
    if (f.resource && f.resource_id) return `${f.resource}:${f.resource_id}`;
    if (f.resource) return f.resource;
    return t('meshPeers.noTarget', 'bez cíle');
  };
  const counts = findings.reduce<Record<string, number>>((acc, f) => {
    acc[f.severity] = (acc[f.severity] ?? 0) + 1;
    return acc;
  }, {});
  const visibleFindings = findings.slice(0, 200);

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Shield}>Security audit ({findings.length})</CardTitle>
        <button
          onClick={() => runScan.mutate()}
          disabled={runScan.isPending}
          className='flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white'
        >
          <RefreshCw size={12} /> {t('meshPeers.spustitScan')}
        </button>
      </CardHeader>
      <CardContent>
        <div className='flex flex-wrap gap-2 mb-3'>
          {['critical', 'high', 'warning', 'med', 'low', 'info'].map((s) =>
            counts[s]
              ? (
                <span
                  key={s}
                  className={`text-[10px] px-2 py-1 rounded-md border ${severityMeta(s).badge}`}
                >
                  {severityMeta(s).label} {counts[s]}
                </span>
              )
              : null
          )}
        </div>
        <div className='space-y-2'>
          {visibleFindings.map((f, index) => {
            const fmt = (iso?: string | null) => {
              if (!iso) return '—';
              return new Date(iso).toLocaleString('cs-CZ', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });
            };
            const age = (iso?: string | null) => {
              if (!iso) return '';
              const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
              if (s < 60) return `${s}s`;
              if (s < 3600) return `${Math.floor(s / 60)}m`;
              if (s < 86400) return `${Math.floor(s / 3600)}h`;
              return `${Math.floor(s / 86400)}d`;
            };
            const stale = f.last_seen
              ? (Date.now() - new Date(f.last_seen).getTime()) > 24 * 3600 * 1000
              : false;
            const meta = severityMeta(f.severity);
            return (
              <div
                key={f.id ?? `${f.source ?? 'finding'}-${f.code}-${targetLabel(f)}-${index}`}
                className='relative overflow-hidden rounded-lg border border-gray-800 bg-gray-900/30 px-3 py-2 text-sm hover:border-gray-700'
              >
                <span className={`absolute left-0 top-0 h-full w-1 ${meta.bar}`} />
                <div className='flex items-start gap-2 pl-1'>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${meta.badge}`}
                  >
                    {meta.label}
                  </span>
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2 min-w-0'>
                      <span className='text-xs text-white break-words'>{f.message}</span>
                      {f.resolved_at && (
                        <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25'>
                          resolved
                        </span>
                      )}
                      {!f.resolved_at && stale && (
                        <span
                          className='text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/25'
                          title={t('meshPeers.lastSeen24hScan')}
                        >
                          stale
                        </span>
                      )}
                    </div>
                    <div className='mt-1 flex items-center gap-2 text-[10px] text-gray-500 min-w-0'>
                      <span className='font-mono text-gray-400 truncate' title={targetLabel(f)}>
                        {targetLabel(f)}
                      </span>
                      <span className='text-gray-700'>·</span>
                      <span className='font-mono'>{f.source ?? 'security'}</span>
                      <span className='text-gray-700'>·</span>
                      <span className='font-mono'>{f.code}</span>
                      <span className='text-gray-700'>·</span>
                      <span title={t('meshPeers.lastSeenTitle', 'naposledy viděno: {{when}}', { when: fmt(f.last_seen) })}>
                        {fmt(f.last_seen)} {age(f.last_seen) && t('meshPeers.agoParen', '({{age}} zpět)', { age: age(f.last_seen) })}
                      </span>
                    </div>
                    {f.code === 'TASK_REPEATED_FAILURE' && f.resource_id && (
                      <TaskFailureDetails taskId={f.resource_id} details={f.details} />
                    )}
                  </div>
                  {f.action_url && (
                    <Link
                      to={f.action_url}
                      className='flex-shrink-0 text-[10px] text-blue-300 hover:text-blue-200 underline decoration-dotted'
                    >
                      Opravit
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
          {findings.length > visibleFindings.length && (
            <div className='text-[11px] text-gray-500 px-1 pt-1'>
              Zobrazeno prvních {visibleFindings.length} z {findings.length} {t('meshpeers.nalezu', 'nálezů.')}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function MeshPeersPanel() {
  // Bez PageHeader — ModuleWorkspace / widget shell kreslí hlavičku stránky.
  return <FleetPanel />;
}
